import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useUIStore } from "../stores/ui-store";
import { useShallow } from "zustand/shallow";
import { useTranslation } from "react-i18next";
import {
  adminGrantUserPermissions,
  adminRevokeUserPermissions,
  grantUserPermission,
  revokeUserPermission,
} from "../api/user.api";
import {
  CreatePermissionsBody,
  RevokePermissionsBody,
} from "../api/models/permission.models";
import { UIState } from "../types/ui-types";
import {
  adminFetchUserPermissions,
  fetchUserPermissions,
} from "../api/userRights.api";

const usePermissions = ({
  userId,
  companyId,
  organizationId,
}: {
  userId: number;
  companyId?: number;
  organizationId?: number;
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [bearerToken, toast] = useUIStore(
    useShallow((state: UIState) => [state.bearerToken, state.toastRef])
  );

  const {
    data: permissionsData,
    error: permissionsError,
    isLoading: permissionsIsLoading,
  } = useQuery({
    queryKey: ["userPermissions", userId],
    queryFn: () =>
      companyId
        ? fetchUserPermissions(
            bearerToken as string,
            userId,
            companyId as number,
            organizationId as number
          )
        : adminFetchUserPermissions(bearerToken as string, userId),
    enabled: !!userId,
  });

  const grantPermissionsMutation = useMutation({
    mutationFn: (payload: CreatePermissionsBody) =>
      companyId
        ? grantUserPermission(bearerToken as string, payload, companyId)
        : adminGrantUserPermissions(bearerToken as string, payload),
    onError: (error) => {
      console.error("Failed to grant permissions:", error);
      toast?.current?.show({
        severity: "error",
        detail: t("text-permissions-grant-failed"),
        life: 3000,
      });
    },
    onSuccess: () => {
      toast?.current?.show({
        severity: "success",
        detail: t("text-permissions-granted"),
        life: 3000,
      });
    },
  });

  const grantPermissions = async (payload: CreatePermissionsBody) => {
    const result = await grantPermissionsMutation.mutateAsync(payload);
    return result;
  };

  const revokePermissionsMutation = useMutation({
    mutationFn: (payload: RevokePermissionsBody) =>
      companyId
        ? revokeUserPermission(bearerToken as string, payload, companyId)
        : adminRevokeUserPermissions(bearerToken as string, payload),
    onError: (error) => {
      console.error("Failed to revoke permissions:", error);
      toast?.current?.show({
        severity: "error",
        detail: t("text-permissions-revoke-failed"),
        life: 3000,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["userPermissions", userId],
      });
    },
  });

  const revokePermissions = async (payload: RevokePermissionsBody) => {
    const result = await revokePermissionsMutation.mutateAsync(payload);
    return result;
  };

  return {
    grantPermissions,
    isGrantingPermissions: grantPermissionsMutation.isPending,
    error: grantPermissionsMutation.error,
    permissionsData,
    permissionsError,
    permissionsIsLoading,
    revokePermissions,
    isRevokingPermissions: revokePermissionsMutation.isPending,
  };
};

export default usePermissions;
